import { useEffect, useState } from "react";
import { Crown, RefreshCw, Wifi, WifiOff, IndianRupee, Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api, formatApiError } from "@/lib/api";

export default function NoblePacks() {
  const [tiers, setTiers] = useState([]);
  const [source, setSource] = useState("");
  const [loading, setLoading] = useState(true);
  const [pricing, setPricing] = useState([]);
  const [savingPrice, setSavingPrice] = useState(false);

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/noble/config");
      setTiers(data?.tiers || []);
      setSource(data?.source || "");
      const priceRes = await api.get("/admin/noble/pricing");
      setPricing((priceRes.data?.tiers || []).map((t) => ({
        key: t.key,
        label: t.label,
        first: t.price?.first || "",
        renew: t.price?.renew || "",
      })));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const setPrice = (key, field, value) => {
    setPricing((prev) => prev.map((p) => (p.key === key ? { ...p, [field]: value } : p)));
  };

  const savePricing = async () => {
    setSavingPrice(true);
    try {
      const prices = {};
      pricing.forEach((p) => {
        prices[p.key] = { first: Number(p.first) || 0, renew: Number(p.renew) || 0 };
      });
      await api.post("/admin/noble/pricing", { prices });
      toast.success("Noble user pricing saved");
      refresh();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSavingPrice(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const isLive = source === "live";

  return (
    <div className="space-y-6" data-testid="noble-packs-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[10px] font-bold uppercase tracking-[0.25em] text-zinc-500">Qwick Live</p>
          <h1 className="font-display text-3xl font-semibold text-zinc-900 mt-1">Noble Packs</h1>
          <p className="text-zinc-500 mt-1 max-w-2xl">
            Noble tiers, pack IDs and diamond costs are pulled live from Qwick Live (Noble.ListAllNobleConf) and used
            automatically for recharges — no manual setup needed.
          </p>
        </div>
        <Button onClick={refresh} disabled={loading} variant="outline" data-testid="refresh-noble-packs">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      <div
        className={`inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold ${
          isLive ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"
        }`}
        data-testid="noble-source-badge"
      >
        {isLive ? <Wifi className="w-3.5 h-3.5" /> : <WifiOff className="w-3.5 h-3.5" />}
        {isLive ? "Synced live from Qwick Live" : "Showing fallback values (live session unavailable — check Session Cookie)"}
      </div>

      <div className="bg-white border border-zinc-200 rounded-lg overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-zinc-50 border-b border-zinc-200">
              {["Tier", "noble_type", "First-time 💎", "First pack id", "Renewal 💎", "Renewal pack id"].map((h) => (
                <th key={h} className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-6 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {tiers.length === 0 && (
              <tr><td colSpan="6" className="px-6 py-12 text-center text-zinc-400 text-sm">{loading ? "Loading…" : "No tiers available"}</td></tr>
            )}
            {tiers.map((t) => (
              <tr key={t.key} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`noble-tier-row-${t.key}`}>
                <td className="px-6 py-4 text-sm font-medium text-zinc-900">
                  <span className="inline-flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" />{t.label}</span>
                </td>
                <td className="px-6 py-4 text-sm text-zinc-700 font-mono">{t.noble_type}</td>
                <td className="px-6 py-4 text-sm text-zinc-900 tabular-nums font-mono">{(t.first?.diamond ?? 0).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-zinc-500 font-mono">{t.first?.turnover_pack_id || "—"}</td>
                <td className="px-6 py-4 text-sm text-zinc-900 tabular-nums font-mono">{(t.renew?.diamond ?? 0).toLocaleString()}</td>
                <td className="px-6 py-4 text-sm text-zinc-500 font-mono">{t.renew?.turnover_pack_id || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* ---------- User-facing INR pricing ---------- */}
      <div className="bg-white border border-zinc-200 rounded-lg p-6" data-testid="noble-pricing-editor">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="font-display text-xl font-semibold text-zinc-900 flex items-center gap-2">
              <IndianRupee className="w-5 h-5 text-amber-500" /> User Noble Pricing (₹)
            </h2>
            <p className="text-zinc-500 mt-1 text-sm max-w-2xl">
              Set the price end-users pay to buy each noble title on the recharge page. A tier with ₹0
              for both First-time and Renewal is hidden from users.
            </p>
          </div>
          <Button onClick={savePricing} disabled={savingPrice || loading} className="bg-amber-500 hover:bg-amber-600" data-testid="save-noble-pricing">
            <Save className={`w-4 h-4 mr-2 ${savingPrice ? "animate-pulse" : ""}`} /> {savingPrice ? "Saving…" : "Save pricing"}
          </Button>
        </div>

        <div className="mt-4 overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-zinc-50 border-b border-zinc-200">
                {["Tier", "First-time ₹", "Renewal ₹"].map((h) => (
                  <th key={h} className="text-left text-xs font-semibold text-zinc-500 uppercase tracking-wider px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pricing.map((p) => (
                <tr key={p.key} className="border-b border-zinc-100" data-testid={`noble-price-row-${p.key}`}>
                  <td className="px-4 py-3 text-sm font-medium text-zinc-900">
                    <span className="inline-flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" />{p.label}</span>
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      type="number" min="0" value={p.first}
                      onChange={(e) => setPrice(p.key, "first", e.target.value)}
                      placeholder="0" className="max-w-[140px]"
                      data-testid={`noble-price-first-${p.key}`}
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Input
                      type="number" min="0" value={p.renew}
                      onChange={(e) => setPrice(p.key, "renew", e.target.value)}
                      placeholder="0" className="max-w-[140px]"
                      data-testid={`noble-price-renew-${p.key}`}
                    />
                  </td>
                </tr>
              ))}
              {pricing.length === 0 && (
                <tr><td colSpan="3" className="px-4 py-8 text-center text-zinc-400 text-sm">{loading ? "Loading…" : "No tiers"}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
